// Shared predicate partitioner — the engine-owned boolean split.
//
// A source op's `:filter` predicate is split into a part the provider's
// backend can apply natively (the "pushed" subtree, translated to the
// provider's native filter language) and a part the engine must run
// client-side (the "residual" subtree, replayed via the standard
// `filter` op path). The boolean split is identical for every provider —
// only "is this leaf pushable?" and "how does it translate?" vary — so
// the split lives here ONCE and each provider plugs in two per-query
// closures.
//
// Splitting rules (the SSOT both CDF and every future provider share):
//
//  - **Top-level `and`**: split children independently. Each child goes
//    to the pushed side or the residual side; the two sides are
//    re-wrapped with `and` (single children unwrapped, empty sides
//    omitted).
//  - **Top-level `or` / `not` / leaf**: all-or-nothing. The subtree
//    pushes only if `isPushable` is true for it as a whole. Boolean logic
//    forbids a safe partial split — pushing one arm of an `(or …)` alone
//    would change the truth table.
//
// CDF's `partitionFilter` (`ops/knowledge-graph/pushdown.ts`) is now one
// caller of this helper: it supplies closures that bind the resolved
// `DataModelView` and delegate to `isPushableNode` / `translatePredicateToCdf`.

import { type AstInput, andChildren, isAndCall } from '../normalize.ts';
import type { Schema } from '../schema/types.ts';

/**
 * The two per-query closures a provider supplies to drive the split.
 * They receive raw AST subtrees + the input schema — never a flattened
 * leaf — so relation-subpaths, unary leaves, and direct-relation typing
 * survive the partition.
 *
 * The closures are CLOSURES, not stateless `(node, schema)` hooks,
 * because a provider's native translation genuinely needs per-query
 * context the engine has no access to (CDF binds the resolved
 * `DataModelView` so `translate` can build DMS `nested` scope paths).
 *
 * @typeParam NativeType — the provider's native filter shape `translate`
 *   produces (CDF: the DMS instance-query filter JSON).
 */
export type PartitionHooks<NativeType = unknown> = {
  /** True iff the provider's backend can apply `node` natively as a whole. */
  readonly isPushable: (node: AstInput, schema: Schema) => boolean;
  /** Translate a pushable subtree into the provider's native filter shape. */
  readonly translate: (pushed: AstInput, schema: Schema) => NativeType;
};

/**
 * The outcome of a partition: the residual predicate the engine runs
 * client-side (as a `filter` op) and the provider's native filter for
 * the pushed part. Both are optional — a fully-pushable predicate has no
 * `residual`; a fully-residual predicate has no `native`; an absent
 * predicate has neither.
 *
 * @typeParam NativeType — the provider's native filter shape.
 */
export type PartitionResult<NativeType = unknown> = {
  /** The non-pushable subtree, as a raw predicate AST for client-side replay. */
  readonly residual?: AstInput;
  /** The provider's native filter for the pushable subtree. */
  readonly native?: NativeType;
};

/**
 * Wrap `children` as `(and …)`; return a single child unwrapped, and
 * `undefined` when empty. The SSOT for re-assembling a per-child split —
 * shared by both partition sides so an empty side is dropped and a
 * single-child side is never needlessly wrapped.
 */
const wrapAnd = (children: readonly AstInput[]): AstInput | undefined => {
  if (children.length === 0) {
    return undefined;
  }
  if (children.length === 1) {
    return children[0] as AstInput;
  }
  return { name: 'and', params: [...children] };
};

/**
 * Split `predicate` into a pushable subtree and a residual subtree using
 * the provider `isPushable` closure. Returns raw AST for both sides;
 * `translate` is applied once to the assembled pushable subtree at the
 * top of {@link partitionPredicate}. Recurses per child through a
 * top-level `and`; `or` / `not` / leaf are all-or-nothing.
 */
const splitPredicate = (
  predicate: AstInput,
  schema: Schema,
  isPushable: (node: AstInput, schema: Schema) => boolean,
): { readonly pushed?: AstInput; readonly residual?: AstInput } => {
  // Top-level `and`: split each child independently, then re-wrap.
  if (isAndCall(predicate)) {
    const splits = andChildren(predicate).map((p) => splitPredicate(p, schema, isPushable));
    const pushedChildren = splits.flatMap((s) => (s.pushed === undefined ? [] : [s.pushed]));
    const residualChildren = splits.flatMap((s) => (s.residual === undefined ? [] : [s.residual]));
    return { pushed: wrapAnd(pushedChildren), residual: wrapAnd(residualChildren) };
  }

  // Otherwise: all-or-nothing.
  return isPushable(predicate, schema) ? { pushed: predicate } : { residual: predicate };
};

/**
 * Partition a source op's raw `:filter` predicate into the provider's
 * native filter (the pushed part) plus a residual predicate the engine
 * runs client-side. The engine owns ONLY the boolean and/or/not split +
 * legality; the provider owns pushability + native translation via the
 * two per-query closures in `hooks`.
 *
 * An absent `predicate` yields an empty result (neither side). The
 * pushable subtree, if any, is translated ONCE here so a provider's
 * `translate` always sees a single assembled subtree, not per-leaf
 * fragments.
 *
 * @typeParam NativeType — the provider's native filter shape.
 */
export const partitionPredicate = <NativeType>(
  predicate: AstInput | undefined,
  schema: Schema,
  hooks: PartitionHooks<NativeType>,
): PartitionResult<NativeType> => {
  if (predicate === undefined) {
    return {};
  }
  const { pushed, residual } = splitPredicate(predicate, schema, hooks.isPushable);
  return {
    ...(pushed === undefined ? {} : { native: hooks.translate(pushed, schema) }),
    ...(residual === undefined ? {} : { residual }),
  };
};
